// src/SSYCalculator.js
import React, { useState } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import FAQSchema from '../../FAQSchema';
import AdBanner from '../AdPage/AdBanner';

// Register necessary components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend
);

const SSYCalculator = () => {
  const [yearlyInvestment, setYearlyInvestment] = useState(50000);
  const [girlAge, setGirlAge] = useState(5);
  const [startPeriod, setStartPeriod] = useState(2024);
  const [totalInvestment, setTotalInvestment] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [maturityYear, setMaturityYear] = useState(0);
  const [maturityValue, setMaturityValue] = useState(0);

  const interestRate = 8; // Fixed interest rate for SSY

  // const calculateMaturity = () => {
  //   const investmentPeriod = Math.min(15, Math.max(0, 21 - girlAge));
  //   const maturityYear = startPeriod + investmentPeriod;
  //   let totalInvestment = 0;
  //   let totalInterest = 0;
  //   let accumulatedAmount = 0;

  //   for (let year = 0; year < investmentPeriod; year++) {
  //     accumulatedAmount += yearlyInvestment;
  //     totalInvestment += yearlyInvestment;
  //     accumulatedAmount += accumulatedAmount * (interestRate / 100);
  //   }

  //   totalInterest = accumulatedAmount - totalInvestment;
  //   const maturityValue = accumulatedAmount;

  //   setTotalInvestment(totalInvestment);
  //   setTotalInterest(totalInterest);
  //   setMaturityYear(maturityYear);
  //   setMaturityValue(maturityValue.toFixed(2));
  // };
  const calculateMaturity = () => {
    const investmentPeriod = Math.min(15, Math.max(0, 21 - girlAge));
    const postMaturityPeriod = 21 - investmentPeriod; // Remaining years till maturity
    const maturityYear = startPeriod + 21;
    let totalInvestment = 0;
    let balance = 0;
  
    // 15 years of investment
    for (let year = 0; year < investmentPeriod; year++) {
      balance = (balance + yearlyInvestment) * (1 + interestRate / 100);
      totalInvestment += yearlyInvestment;
    }
  
    // 6 years of compounding after investment stops
    for (let year = 0; year < postMaturityPeriod; year++) {
      balance = balance * (1 + interestRate / 100);
    }
  
    const totalInterest = balance - totalInvestment;
  
    setTotalInvestment(totalInvestment);
    setTotalInterest(totalInterest);
    setMaturityYear(maturityYear);
    setMaturityValue(balance.toFixed(2));
  };
  
  const handleYearlyInvestmentChange = (e) => {
    const value = e.target.value;
    setYearlyInvestment(value === '' ? 0 : parseInt(value));
  };

  const handleGirlAgeChange = (e) => {
    const value = e.target.value;
    setGirlAge(value === '' ? 0 : parseInt(value));
  };

  const handleStartPeriodChange = (e) => {
    const value = e.target.value;
    setStartPeriod(value === '' ? 0 : parseInt(value));
  };

  const data = {
    labels: ['Total Investment', 'Total Interest'],
    datasets: [
      {
        label: 'SSY Breakdown',
        data: [totalInvestment, totalInterest],
        backgroundColor: ['#36A2EB', '#FF6384'],
      },
    ],
  };
function Icon({ id, open }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
        >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
    );
  }
  
  
    const [activeIndex, setActiveIndex] = useState(0);
  
     
    const faqs = [
      {
        question: 'What is the current interest rate for SSY?',
        answer: 'As of May 2024, the interest rate is 8.2% per annum.',
      },
      {
        question: 'Is there any fee to use the Sukanya Samriddhi Calculator?',
        answer: 'No, most SSY calculators are available online for free use.',
      },
      {
        question: 'Can I make monthly contributions instead of yearly?',
        answer: 'Yes, contributions can be made monthly, quarterly, or yearly.',
      },
      {
        question: 'What is the minimum and maximum contribution allowed per year?',
        answer: 'The minimum is ₹250 and the maximum is ₹1.5 lakh.',
      },
      {
        question: 'How many SSY accounts can a family open?',
        answer: 'Two accounts per family, one for each girl child.',
      },
      {
        question: 'Is SSY suitable for NRIs?',
        answer: 'No, only Indian residents can invest in the SSY scheme.',
      },
      {
        question: 'What happens if I miss contributing in a year?',
        answer: 'The account becomes inactive but can be revived later.',
      },
      {
        question: 'Can the SSY account be transferred?',
        answer: 'Yes, it can be transferred between banks or post offices across India.',
      },
      {
        question: 'Who can withdraw the maturity amount?',
        answer: 'Only the girl child, upon reaching the age of 21.',
      },
      {
        question: 'What documents are required for partial withdrawal?',
        answer: 'Age proof, Class 10 certificate, and admission/marriage certificate.',
      },
      {
        question: 'Is the maturity amount taxed?',
        answer: 'No, the entire amount including interest is tax-exempt.',
      },
      {
        question: 'Can I continue contributions after 14 years?',
        answer: 'No, deposits are allowed only for the first 14 years.',
      },
      {
        question: 'Can twins or triplets have individual SSY accounts?',
        answer: 'Yes, in such cases, more than two accounts are allowed with proper documentation.',
      },
      {
        question: 'How does the calculator compute compound interest?',
        answer: 'It uses the compound interest formula with annual compounding.',
      },
      {
        question: 'Is SSY better than a child education mutual fund?',
        answer: 'SSY is safer with guaranteed returns, while mutual funds offer potentially higher returns with market-linked risks.',
      },
    ];
    
    const toggleAccordion = (index) => {
      if (activeIndex === index) {
          // If the clicked question is already expanded, collapse it
          setActiveIndex(null);
      } else {
          // If the clicked question is not expanded, expand it
          setActiveIndex(index);
      };
    }
  return (
    <>
     <Helmet>
         <title>Sukanya Samriddhi Yojana Calculator – Estimate Maturity Amount</title>
        <meta  name='description' content='Calculate your Sukanya Samriddhi Yojana returns with this SSY calculator. Plan savings for your daughter’s future.'/>
        <meta name='keywords' content='sukanya samriddhi yojana calculator,ssy calculator,sukanya samriddhi calculator,sukanya calculator' />
        <link rel="canonical" href="https://thefinmaster.com/financial-tools-calculators/sukanya-samriddhi-yojana-calculator" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
        <meta name="robots" content="max-video-preview:-1" />
     </Helmet>
     <FAQSchema faqs={faqs} />
     <div className="w-full flex justify-center items-center px-4 mt-20">
  <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
  <AdBanner displayOrder="1" />
  </div>
</div>
{/*      
     <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
      <ol className="list-none p-0 inline-flex">
        <li className="flex items-center">
          <Link to="/" className="text-blue-600 hover:text-blue-800 ">Home</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
        <li className="flex items-center">
          <Link to="/financial-tools-calculators" className="text-blue-600 hover:text-blue-800">Financial Tools Calculators</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
          <li className="flex items-center">
          <span className="text-gray-500 ">Sukanya Samriddhi Yojana Calculator</span>
        </li> 
      
      </ol>
    </nav> */}
    <div className='py-4'>
    <div className="container mx-auto p-4 shadow-xl rounded-md  ">
      <h1 className="text-2xl font-bold mb-4">Sukanya Samriddhi Yojana Calculator</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Yearly Investment</label>
            <div className="flex items-center">
              <input
                type="number"
                min="0"
                max="150000"
                value={yearlyInvestment}
                onChange={handleYearlyInvestmentChange}
                className="w-1/2 p-2 border rounded mr-2"
              />
              <span className="text-gray-700">₹ {yearlyInvestment.toLocaleString()}</span>
            </div>
            <input
              type="range"
              min="0"
              max="150000"
              value={yearlyInvestment}
              onChange={(e) => setYearlyInvestment(parseInt(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Girl's Age</label>
            <div className="flex items-center">
              <input
                type="number"
                min="0"
                max="10"
                value={girlAge}
                onChange={handleGirlAgeChange}
                className="w-1/2 p-2 border rounded mr-2"
              />
              <span className="text-gray-700">{girlAge} Yr</span>
            </div>
            <input
              type="range"
              min="0"
              max="10"
              value={girlAge}
              onChange={(e) => setGirlAge(parseInt(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Start Period</label>
            <div className="flex items-center">
              <input
                type="number"
                min="2024"
                max="2099"
                value={startPeriod}
                onChange={handleStartPeriodChange}
                className="w-1/2 p-2 border rounded mr-2"
              />
              <span className="text-gray-700">{startPeriod}</span>
            </div>
            <input
              type="range"
              min="2024"
              max="2099"
              value={startPeriod}
              onChange={(e) => setStartPeriod(parseInt(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <button
            onClick={calculateMaturity}
            className="bg-blue-500 text-white px-4 py-2 rounded"
          >
            Calculate
          </button>
        </div>
        <div>
          <div className="mb-4">
            <h2 className="text-xl font-bold">Results</h2>
            <p>Total Investment: ₹ {totalInvestment.toLocaleString()}</p>
            <p>Total Interest: ₹ {totalInterest.toLocaleString()}</p>
            <p>Maturity Year: {maturityYear}</p>
            <p>Maturity Value: ₹ {maturityValue.toLocaleString()}</p>
          </div>
          <div>
            <h2 className="text-xl font-bold">Breakdown</h2>
            <div className="flex justify-center">
              <Pie data={data} options={{ maintainAspectRatio: false }} width={300} height={300} />
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>

    
    <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6" >
    <div className="w-full md:w-2/3 p-4 mx-auto">
    <div className="container mx-auto py-4">
          <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
            What is Sukanya Samriddhi Yojana (SSY)?
          </h2>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            Sukanya Samriddhi Yojana is a government-backed small savings scheme aimed at securing the financial future of a girl child. Introduced in 2015 under the Beti Bachao, Beti Padhao initiative, this scheme encourages parents or legal guardians to invest in their daughter’s future education and marriage expenses.
          </p>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            An SSY account can be opened in any authorized commercial bank or India Post branch. The key advantage of this scheme is the high rate of interest (currently 8.2% as of May 2024) and tax exemption benefits under Section 80C of the Income Tax Act.
          </p>
</div>
          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Who Can Open a Sukanya Samriddhi Account?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              To benefit from the scheme or use the Sukanya Samriddhi Yojana Calculator meaningfully, certain eligibility criteria must be met:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>The account can only be opened by the biological parents or legal guardians of a girl child.</li>
              <li>The girl child must be below 10 years of age at the time of opening the account.</li>
              <li>A maximum of two accounts can be opened per family, one for each girl child. In case of twins or triplets, exceptions are made.</li>
              <li>The account must be operated by the guardian until the girl turns 18, post which she can manage the account herself.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Why Use a Sukanya Samriddhi Yojana Calculator?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Planning for a child’s future is a long-term commitment. Parents often want to ensure they can meet the high costs of higher education or wedding-related expenses without taking on excessive debt. Here’s how an SSY Calculator can help:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Estimate Maturity Corpus:</strong> Based on the amount invested annually, it helps estimate the total corpus available at maturity (21 years from the account opening date).</li>
              <li><strong>Error-Free Calculations:</strong> Manual calculation of compound interest over 21 years is complex. The calculator automates it for you.</li>
              <li><strong>Flexible Planning:</strong> It enables you to adjust your yearly contributions to meet your target maturity amount.</li>
              <li><strong>Tax Forecasting:</strong> Since SSY is eligible for deductions under Section 80C, the calculator can also help you plan your tax-saving investments better.</li>
              <li><strong>Multiple Iterations:</strong> You can simulate different investment scenarios to choose what suits your budget.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How Does the Sukanya Samriddhi Calculator Work?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The SSY calculator uses the compound interest formula:
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>A = P (1 + r/n) ^ (nt)</strong>
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Where:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>A</strong> = Final amount</li>
              <li><strong>P</strong> = Principal contribution per year</li>
              <li><strong>r</strong> = Annual interest rate (currently 8.2%)</li>
              <li><strong>n</strong> = Number of times interest is compounded annually (once for SSY)</li>
              <li><strong>t</strong> = Total investment period (21 years, with contributions for the first 14 years)</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              It takes into account your yearly contributions and the interest accrued to give you the final maturity amount.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How to Use the Sukanya Samriddhi Yojana Calculator Online?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Using the calculator is simple and intuitive. Here's how to go about it:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Enter Annual Investment</strong> – Mention how much you intend to contribute each year (min ₹250 and max ₹1.5 lakh per annum).</li>
              <li><strong>Enter Girl Child’s Current Age</strong> – This helps the calculator determine the remaining years to maturity.</li>
              <li><strong>Enter the Start Year of Investment</strong> – To estimate the maturity year correctly.</li>
              <li><strong>Calculate</strong> – The tool displays the maturity year, maturity amount, and total contributions made.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Benefits of Using the Sukanya Samriddhi Yojana Calculator
            </h2>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Shows the exact maturity year and projected corpus.</li>
              <li>Allows goal-based financial planning for education or marriage.</li>
              <li>Helps track if you're investing enough to meet future expenses.</li>
              <li>Calculates interest earned separately for transparency.</li>
              <li>Encourages disciplined investing habits.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Contribution Rules Under SSY
            </h2>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Minimum Deposit:</strong> ₹250 annually</li>
              <li><strong>Maximum Deposit:</strong> ₹1.5 lakh annually</li>
              <li><strong>Contributions must be made for 14 years from account opening.</strong></li>
              <li><strong>The account continues to earn interest for the full 21-year tenure even if no deposits are made after year 14.</strong></li>
              <li><strong>Failure to make a yearly deposit results in the account being deactivated, which can later be revived by paying a small penalty along with the minimum contribution.</strong></li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Partial Withdrawal and Maturity Rules
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Once the girl child turns 18, certain withdrawals are allowed under the following circumstances:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text Enables the user to adjust their yearly contributions to meet their target maturity amount.xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Higher Education:</strong> Partial withdrawal up to 50% of the balance is permitted if the child has cleared Class 10 and is pursuing higher education.</li>
              <li><strong>Marriage Expenses:</strong> 100% withdrawal is allowed after she turns 18 and submits an affidavit confirming that she is a legal adult.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              At the end of 21 years from the account opening date, the full corpus can be withdrawn tax-free, making it ideal for large financial needs like education abroad or marriage.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              What Happens if You Stop Investing?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              If contributions stop before 14 years:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>The account becomes inactive.</li>
              <li>Interest continues to accrue on the existing amount.</li>
              <li>The account can be revived with a penalty of ₹50 and payment of pending contributions.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Even with discontinued investments, the scheme still benefits from compound growth, although the final corpus will be lower.
            </p>
          </div>
          <div className="w-full flex justify-center items-center my-6 px-4">
          <div className="w-full max-w-[984px]">

     
        <AdBanner displayOrder="3" />
        </div>
         
        </div>
          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Tax Benefits of SSY
            </h2>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Deduction under Section 80C:</strong> Investments up to ₹1.5 lakh qualify.</li>
              <li><strong>Tax-Free Interest:</strong> Annual interest earned is not taxable.</li>
              <li><strong>Tax-Free Maturity:</strong> The entire maturity amount is exempt from taxation.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              SSY is one of the very few schemes to offer EEE (Exempt-Exempt-Exempt) tax benefits.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How Can the Maturity Corpus Be Used?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The maturity amount is usually used for:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Higher education fees and living expenses</li>
              <li>Funding foreign education</li>
              <li>Marriage expenses</li>
              <li>Emergency financial requirements (post maturity)</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Required documents for corpus withdrawal:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Application form by the girl child.</li>
              <li>Proof of identity and age.</li>
              <li>Admission or marriage certificate (for partial withdrawals).</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              SSY vs Other Long-Term Investment Options
            </h2>
            {/* <div className="overflow-x-auto mt-4">
              <table className="table-auto w-full border-collapse border border-gray-300">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="px-4 py-2 text-left">Feature</th>
                    <th className="px-4 py-2 text-left">SSY</th>
                    <th className="px-4 py-2 text-left">PPF</th>
                    <th className="px-4 py-2 text-left">Mutual Funds (SIP)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-300 text-gray-600">
                  <tr>
                    <td className="px-4 py-2">Interest Rate</td>
                    <td className="px-4 py-2">8.2% (fixed)</td>
                    <td className="px-4 py-2">~7.1%</td>
                    <td className="px-4 py-2">Varies (10–15% avg)</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Tax Benefit</td>
                    <td className="px-4 py-2">Section 80C + EEE</td>
                    <td className="px-4 py-2">Section 80C + EEE</td>
                    <td className="px-4 py-2">Only ELSS under 80C</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Lock-in Period</td>
                    <td className="px-4 py-2">21 years</td>
                    <td className="px-4 py-2">15 years</td>
                    <td className="px-4 py-2">3 years (ELSS)</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Risk Level</td>
                    <td className="px-4 py-2">Very Low (Govt-backed)</td>
                    <td className="px-4 py-2">Low (Govt-backed)</td>
                    <td className="px-4 py-2">Moderate to High</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2">Goal Orientation</td>
                    <td className="px-4 py-2">Child-specific</td>
                    <td className="px-4 py-2">General</td>
                    <td className="px-4 py-2">General</td>
                  </tr>
                </tbody>
              </table>
            </div> */}
            <div className="max-w-4xl mx-auto overflow-hidden rounded-lg border border-gray-300 shadow mt-6">
    {/* Header */}
    <div className="hidden md:grid grid-cols-4 bg-gray-800 text-white text-sm font-semibold text-center">
      <div className="px-3 py-2 border-r">Feature</div>
      <div className="px-3 py-2 border-r">SSY	</div>
      <div className="px-3 py-2  border-r">	PPF</div>
      <div className="px-3 py-2">Mutual Funds (SIP)</div>
    </div>

    {/* Data rows for mobile (stacked) and desktop (grid) */}
    <div className="divide-y divide-gray-200 text-sm md:text-base text-gray-600 ">
      {/* Row Template */}
      {[
        {
          feature: "Interest Rate",
          ssy: "8.2% (fixed)",
          ppf: "~7.1%",
          mutal: "Varies (10–15% avg)",
        },
     
        {
          feature: "Tax Benefit",
          ssy: "Section 80C + EEE",
          ppf: "Section 80C + EEE",
          mutal: "Only ELSS under 80C",
        },
        {
          feature: "Lock-in Period",
          ssy: "21 years",
          ppf: "15 years",
          mutal: "3 years (ELSS)",
        },
        {
          feature: "Risk Level",
          ssy: "	Very Low (Govt-backed)",
          ppf: "Low (Govt-backed)",
          mutal: "Moderate to High",
        },
        {
          feature: "Goal Orientation",
          ssy: "	Child-specific",
          ppf: "General",
          mutal: "	General",
        },
      
      ].map((row, idx) => (
        <div
          key={idx}
          className="block md:grid md:grid-cols-4 hover:bg-gray-50 transition"
        >
          {/* Mobile version */}
          <div className="block md:hidden px-4 py-3">
            <p className="font-semibold">Feature: <span className="font-normal">{row.feature}</span></p>
            <p className="font-semibold">SSY: <span className="font-normal">{row.ssy}</span></p>
            <p className="font-semibold">PPF: <span className="font-normal">{row.ppf}</span></p>
            <p className="font-semibold">Mutal Funds (SIP): <span className="font-normal">{row.mutal}</span></p>

          </div>

          {/* Desktop version */}
          <div className="hidden md:block px-4 py-3 border-r">{row.feature}</div>
          <div className="hidden md:block px-4 py-3 border-r">{row.ssy}</div>
          <div className="hidden md:block px-4 py-3 border-r">{row.ppf}</div>
          <div className="hidden md:block px-4 py-3">{row.mutal}</div>

        </div>
      ))}
    </div>
  </div>
          </div>

          <h2 className="text-xl font-medium text-gray-700 mt-4">
            “Looking to invest? Open an account with TheFinMaster and start investing in Sukanya Samriddhi Yojana for your girl child’s future!”
          </h2>
        </div>

        <div className="w-full md:w-1/3 p-4">
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
              <h2 className="text-lg font-semibold ">Popular Categories</h2>
            </div>
            <div className="p-2 ">
            <ul>
                                <Link to='/financial-tools-calculators/swp-calculator'>
                                    <li className="py-2  hover:bg-slate-100 border-b border-gray-300">SWP Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/mutul-fund-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Mutual Fund Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/sukanya-samriddhi-yojana-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Samriddhi Yojana Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/fd-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">FD Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/rd-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">RD Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/nps-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">NPS Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/hra-exemption-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">HRA Exemption Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/retirement-planning-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Retirement Planning Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">EMI Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/simple-interest-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Simple Interest Calculator</li>

                                </Link><Link to='/financial-tools-calculators/compound-interest-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Compound Interest Calculator</li>

                                </Link><Link to='/financial-tools-calculators/nsc-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">NSC Calculator</li>

                                </Link><Link to='/financial-tools-calculators/step-up-sip-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Step Up Sip Calculator</li>

                                </Link><Link to='/financial-tools-calculators/income-tax-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Income Tax Calculator</li>
                                    
                                </Link><Link to='/financial-tools-calculators/home-loan-emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Home Loan Emi Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/car-loan-emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Car Loan Emi Calculator</li>
                                </Link>
                                {/* {categories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
                            </ul>
                        </div>
                    </div>

                           {/* Ad Banner */}
  <div className="mt-4">
    <div className="bg-white shadow-md rounded-lg overflow-hidden">
      <div className="p-3 w-full h-auto rounded-md object-cover">
        <AdBanner displayOrder="2" />
      </div>
    </div>
  </div>
                    <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
                        <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
                            <h2 className="text-lg font-semibold">More Categories</h2>
                        </div>
                        <div className="p-2">
                        <ul>
                                <Link to='/financial-tools-calculators/gratuity-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Gratuity Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/APY-calculator'>
                                    <li className="py-2 border- hover:bg-slate-100 border-gray-300">APY Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/cagr-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">CAGR Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/gst-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">GST Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/flat-vs-reducing-rate-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Flat Vs Reducing Rate Calculator</li>
                                </Link>
                                {/* <Link to='/financial-tools-calculators/equity-margin-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Equity Margin Calculator</li>
                                </Link> */}
                                <Link to='/financial-tools-calculators/inflation-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Inflation Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/salary-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Salary Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/brokerage-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Brokerage Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/tds-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">TDS Calculator</li>
                                </Link>
                {/* {moreCategories.map(category => (
          <Link key={category.id} to={`/category/${category.id}`}>
            <li className="py-2 border-b border-gray-300">{category.name}</li>
          </Link>
        ))} */}
              </ul>
            </div>
          </div>

        </div>

      </div>


      <div className="max-w-4xl mx-auto py-6 mt-10 px-2">
                <h2 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">Frequently Asked Questions</h2>
                <div className="space-y-4">
                    {faqs.map((faq, index) => (
                        <div key={index} className="border rounded-lg overflow-hidden shadow-md">
                            <button
                                onClick={() => toggleAccordion(index)}
                                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                            >
                                {faq.question}
                                <Icon id={index} open={activeIndex} />
                            </button>
                            {activeIndex === index && (
                                <div className="p-4 bg-gray-100">
                                    <p className="text-gray-700">{faq.answer}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
    </>
  );
};

export default SSYCalculator;
